/**
 * Scribe pieces the Chat tab's "From the scribe today" strip and the Scribe tab share
 * (design scribeVals, L31864).
 */
import type { ScribeActionKind, ScribeNoteType, ScribeSession } from '@aithora/shared';

export const SC_GREEN = '#1F6B52';
export const SC_RED = '#B23A3A';

/** The design's 18px Scribe badge (scribeVals badge()). */
export const scBadge = (c: string, bg?: string, bd?: string) =>
  'flex:0 0 auto;display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:' +
  c + ';background:' + (bg || 'transparent') + ';border:1px solid ' + (bd || 'transparent') + ';';
export const BADGE_SIGNED = scBadge(SC_GREEN, 'rgba(31,107,82,.1)', 'rgba(31,107,82,.3)');
export const BADGE_WAITING = scBadge('#8A4B12', 'rgba(196,122,40,.12)', 'rgba(196,122,40,.35)');
export const BADGE_DRAFTED = scBadge('#2F5C97', 'rgba(47,92,151,.08)', 'rgba(47,92,151,.3)');
export const BADGE_OFF = scBadge('#6B737C', 'transparent', 'rgba(34,39,46,.2)');

/** The design's note-type names, by the server's keys. */
export const TYPE_NAME: Record<ScribeNoteType, string> = {
  resident_note: 'Resident note',
  shift_handoff: 'Shift handoff',
  worklist_note: 'Worklist note',
  incident: 'Incident',
};
export const typeName = (t: string) => TYPE_NAME[t as ScribeNoteType] ?? t.replace(/_/g, ' ');

/** Where each kind of action goes when the note is signed (design scribe lines' route). */
export const ACTION_ROUTE: Record<ScribeActionKind, string> = {
  create_task: 'Worklist task',
  hand_off: 'To the next shift · Worklist',
  message_nurse: 'Care team message',
  message_family: 'Family Portal message',
  mark_done: 'Worklist · closes the task',
  file_incident: 'Guardian · Incidents',
  record_vitals: 'Chart · vitals',
  record_fluids: 'Chart · fluids',
};

const DAY = 86_400_000;
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** '7:42 am' today, 'Yesterday', otherwise '12 Sep' (the design's session times). */
export function whenText(iso: string, now: number): string {
  const d = new Date(iso);
  const n = new Date(now);
  if (now - d.getTime() < 60_000 && now >= d.getTime()) return 'just now';
  if (sameDay(d, n)) {
    const h = d.getHours();
    return `${h % 12 === 0 ? 12 : h % 12}:${String(d.getMinutes()).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
  }
  if (sameDay(d, new Date(now - DAY))) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** One row of "Today's notes" (design SESS). */
export function sessionRow(s: ScribeSession, now: number) {
  const signed = s.status === 'signed';
  const kept = s.actions.filter((a) => a.decision !== 'drop').length;
  const parts: string[] = [];
  if (signed && s.noteType === 'incident' && s.guardianFiled) parts.push('Filed to Guardian');
  else parts.push(`${kept} actions ${signed ? 'sealed' : 'waiting'}`);
  // Audio: said only when the server keeps it (it accepts none yet, so nothing is claimed).
  if (s.audio.accepted) {
    if (signed || s.audio.deleted) parts.push('audio deleted');
    else if (s.audio.deleteBy) parts.push(`audio kept ${Math.max(0, Math.ceil((Date.parse(s.audio.deleteBy) - now) / DAY))} more days`);
  } else parts.push(signed ? 'no audio kept' : 'transcript only');
  return {
    id: s.id,
    who: s.subjectLabel ?? (s.noteType === 'shift_handoff' ? 'Shift handoff' : ''),
    type: typeName(s.noteType),
    state: signed ? 'SIGNED' : 'NEEDS SIGNATURE',
    stStyle: signed ? BADGE_SIGNED : BADGE_WAITING,
    when: whenText(s.createdAt, now),
    note: parts.join(' · '),
  };
}
